import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import api from '../../services/api';
import TeamPlayers from './TeamPlayers';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EditTeamModal = ({
  isOpen,
  toggle,
  editedTeamName,
  editedSchedule,
  editedStart,
  editedEnd,
  editedNotes,
  schedules,
  editingTeam,
  leagueId,
  onTeamNameChange,
  onScheduleChange,
  onStartChange,
  onNotesChange,
  onSave,
  onDeleteClick,
}) => {
  const [activeTab, setActiveTab] = useState('general');
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [timeslots, setTimeslots] = useState([]);
  const [timeslotsLoading, setTimeslotsLoading] = useState(false);
  const [aligningWeeks, setAligningWeeks] = useState(false);
  const [alignWeeksStatus, setAlignWeeksStatus] = useState({ type: '', message: '' });

  const fetchScheduleEvents = useCallback(async () => {
    if (!leagueId || !editingTeam) return;
    setScheduleLoading(true);
    try {
      const res = await api.get(`/league-team-events/?league=${leagueId}&team=${editingTeam}&show_all=true`);
      setScheduleEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching league events:', err);
    } finally {
      setScheduleLoading(false);
    }
  }, [leagueId, editingTeam]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('general');
      setScheduleEvents([]);
      setAlignWeeksStatus({ type: '', message: '' });
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeTab !== 'schedule' || !leagueId || !editingTeam) return;
    fetchScheduleEvents();
  }, [activeTab, fetchScheduleEvents, leagueId, editingTeam]);

  useEffect(() => {
    const assignedSchedule = schedules.find((schedule) => String(schedule.id) === String(editedSchedule));
    const dayValue = assignedSchedule?.day_of_week;
    const hasNumericDay = dayValue !== '' && dayValue !== null && dayValue !== undefined && Number.isInteger(Number(dayValue));
    const day = hasNumericDay
      ? DAYS_OF_WEEK[Number(dayValue)]
      : dayValue;

    if (!editedSchedule || !day) {
      setTimeslots([]);
      return;
    }

    const fetchTimeslots = async () => {
      setTimeslotsLoading(true);
      try {
        const response = await api.get(`/leagues/${leagueId}/available-timeslots/`, {
          params: { day },
          skipAuthRedirect: true,
        });
        setTimeslots(Array.isArray(response.data) ? response.data : response.data?.timeslots || []);
      } catch (error) {
        console.error('Error fetching schedule timeslots:', error);
        setTimeslots([]);
      } finally {
        setTimeslotsLoading(false);
      }
    };

    fetchTimeslots();
  }, [editedSchedule, leagueId, schedules]);

  const formatTimeValue = (time) => (time ? time.slice(0, 5) : '');
  const formatTimeLabel = (time) => (time ? moment(time, ['HH:mm:ss', 'HH:mm']).format('h:mm A') : '');

  const handleAlignWeeks = async () => {
    if (!leagueId || !editingTeam) return;

    setAligningWeeks(true);
    setAlignWeeksStatus({ type: '', message: '' });

    try {
      const response = await api.post('/league-team-events/align-weeks/', {
        league: leagueId,
        team: editingTeam,
      });
      setAlignWeeksStatus({
        type: 'success',
        message: response?.data?.message || 'Weeks aligned successfully.',
      });
      await fetchScheduleEvents();
    } catch (error) {
      const errorData = error.response?.data;
      const message =
        errorData?.detail ||
        errorData?.message ||
        error.message ||
        'Failed to align weeks. Please try again.';
      setAlignWeeksStatus({ type: 'danger', message });
    } finally {
      setAligningWeeks(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Edit Team: {editedTeamName}</ModalHeader>
      <ModalBody>
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              active={activeTab === 'general'}
              onClick={() => setActiveTab('general')}
              style={{ cursor: 'pointer' }}
            >
              General
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={activeTab === 'players'}
              onClick={() => setActiveTab('players')}
              style={{ cursor: 'pointer' }}
            >
              Players
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={activeTab === 'schedule'}
              onClick={() => setActiveTab('schedule')}
              style={{ cursor: 'pointer' }}
            >
              Schedule
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="general">
            <Row className="mb-3">
              <Col xs="7">
                <label className="form-label">Team Name</label>
                <Input
                  type="text"
                  value={editedTeamName}
                  onChange={(e) => onTeamNameChange(e.target.value)}
                  placeholder="Enter new team name"
                />
              </Col>
              <Col xs="5">
                <label className="form-label">Assignments</label>
                <Row className="g-2 align-items-center">
                  <Col xs="3">
                    <label className="form-label mb-0">Schedule</label>
                  </Col>
                  <Col xs="9">
                    <Input
                      type="select"
                      value={editedSchedule}
                      onChange={(e) => onScheduleChange(e.target.value)}
                    >
                      <option value="">-- No Schedule --</option>
                      {schedules.map((schedule) => (
                        <option key={schedule.id} value={schedule.id}>
                          {schedule.title}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col xs="3">
                    <label className="form-label mb-0">Time</label>
                  </Col>
                  <Col xs="9">
                    <Input
                      type="select"
                      value={timeslots.some((timeslot) => formatTimeValue(timeslot.start) === editedStart) ? editedStart : ''}
                      onChange={(e) => onStartChange(e.target.value)}
                      disabled={!editedSchedule || timeslotsLoading || timeslots.length === 0}
                    >
                      <option value="">
                        {timeslotsLoading ? 'Loading timeslots...' : 'Automatically Assigned'}
                      </option>
                      {timeslots.map((timeslot) => (
                        <option key={timeslot.id} value={formatTimeValue(timeslot.start)}>
                          {formatTimeLabel(timeslot.start)} - {formatTimeLabel(timeslot.end)}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs="12">
                <label className="form-label">Notes</label>
                <Input
                  type="textarea"
                  value={editedNotes}
                  onChange={(e) => onNotesChange(e.target.value)}
                  placeholder="Add any notes about this team..."
                  rows="4"
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Button color="primary" onClick={onSave}>
                  Save Changes
                </Button>
              </Col>
            </Row>
          </TabPane>
          <TabPane tabId="players">
            <TeamPlayers teamId={editingTeam} />
          </TabPane>
          <TabPane tabId="schedule">
            {alignWeeksStatus.message && (
              <div className={`alert alert-${alignWeeksStatus.type} py-2`}>
                {alignWeeksStatus.message}
              </div>
            )}
            {scheduleLoading ? (
              <div className="text-center py-4">
                <Spinner size="sm" /> Loading schedule...
              </div>
            ) : scheduleEvents.length === 0 ? (
              <p className="text-muted small">No scheduled events found for this team.</p>
            ) : (
              <Table bordered hover responsive size="sm" className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Week</th>
                    <th>Date</th>
                    <th>Start</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleEvents.map((item) => (
                      <tr key={item.id}>
                        <td>{item.league_date?.week ?? '—'}</td>
                        <td>{item.event?.start ? moment(item.event.start).format('ddd MMM D, YYYY') : '—'}</td>
                        <td>{item.event?.start ? moment(item.event.start).format('h:mm A') : '—'}</td>
                        <td>{item.league_date?.notes || ''}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )}
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        {activeTab === 'general' ? (
          <Button color="danger" outline onClick={onDeleteClick}>
            Remove team and scheduled events from league
          </Button>
        ) : activeTab === 'schedule' ? (
          <Button
            color="primary"
            onClick={handleAlignWeeks}
            disabled={aligningWeeks || !leagueId || !editingTeam}
          >
            {aligningWeeks ? 'Aligning...' : 'Align Weeks'}
          </Button>
        ) : (
          <span />
        )}
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditTeamModal;
